import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NavBar from '../../components/NavBar';
import Breadcrumbs from '../../components/Breadcrumbs';
import StoreCTA from '../../components/StoreCTA';
import '../../styles/gameInfo.css';

function CalcudokuPage() {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      {
        '@type': 'Question',
        'name': 'Is Calcudoku similar to Sudoku?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Calcudoku shares the Latin square constraint with Sudoku (each number appears once per row and column), but adds arithmetic cage constraints. It combines number placement with math operations, making it a unique hybrid of logic and arithmetic.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Do I need to be good at math to solve Calcudoku?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Only basic arithmetic is needed. Calcudoku uses addition, subtraction, multiplication, and division with small numbers. The challenge is logical deduction, not complex math.'
        }
      },
      {
        '@type': 'Question',
        'name': 'What operations are used in Calcudoku cages?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Calcudoku cages use four operations: addition (+), subtraction (-), multiplication (\u00d7), and division (\u00f7). Single-cell cages show the target number directly with no operation.'
        }
      },
      {
        '@type': 'Question',
        'name': 'Can numbers repeat within a Calcudoku cage?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'Numbers can repeat within a cage as long as they don\'t share the same row or column. This is a key difference from some other puzzle types.'
        }
      }
    ]
  };

  const videoGameSchema = {
      "@context": "https://schema.org",
      "@type": "VideoGame",
      "name": "Calcudoku",
      "description": "Arithmetic-cage logic puzzle blending Sudoku-style placement with math constraints.",
      "url": "https://logicpuzzles.ca/games/calcudoku",
      "image": "https://logicpuzzles.ca/calcudoku/icons/Icon-512.png",
      "genre": "Logic Puzzle",
      "gamePlatform": "Web Browser",
      "operatingSystem": "Any (browser-based)",
      "playMode": "SinglePlayer",
      "inLanguage": "en",
      "isAccessibleForFree": true,
      "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "CAD",
          "availability": "https://schema.org/InStock"
      },
      "publisher": {
          "@id": "https://logicpuzzles.ca/#organization"
      },
      "potentialAction": {
          "@type": "PlayAction",
          "target": "https://logicpuzzles.ca/play/calcudoku"
      }
  };

  return (
    <div>
      <Helmet>
        <title>Calcudoku Puzzles - Free Math Logic Puzzles Online | Logic Puzzles CA</title>
        <meta name="description" content="Solve free Calcudoku math cage puzzles! Combine arithmetic with logic in this math puzzle. Fill the grid using addition, subtraction, multiplication, and division cages." />
        <meta name="keywords" content="calcudoku, calcudoku online, free calcudoku, printable calcudoku, math cage puzzle, math logic puzzle, arithmetic puzzle, math puzzle game" />
        <meta property="og:title" content="Calcudoku Puzzles - Free Math Logic Puzzles Online" />
        <meta property="og:description" content="Combine arithmetic with logic! Solve free Calcudoku puzzles with cage constraints using +, -, \u00d7, \u00f7 operations." />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://logicpuzzles.ca/calcudoku/icons/Icon-512.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Calcudoku Puzzles - Free Math Logic Puzzles Online" />
        <meta name="twitter:description" content="Combine arithmetic with logic! Solve free Calcudoku puzzles with cage constraints." />
        <link rel="canonical" href="https://logicpuzzles.ca/games/calcudoku" />
        <script type="application/ld+json">{JSON.stringify(videoGameSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <NavBar />
      <Breadcrumbs categoryName="Logic Games" categoryPath="/logic-games" currentLabel="Calcudoku" />

      <header className="gi-hero">
        <div className="container gi-hero__inner">
          <div>
            <p className="eyebrow">Math Logic Puzzle · Free online</p>
            <h1>Calcudoku Puzzles</h1>
            <p className="gi-hero__lead">Where arithmetic meets logical deduction</p>
            <div className="gi-hero__actions">
              <Link to="/play/calcudoku" className="button">▶ Play now</Link>
              <a href="#how-to-play" className="button secondary">How to solve</a>
              <Link to="/printable/calcudoku" className="button secondary">Printable puzzles</Link>
            </div>
          </div>
          <div className="gi-hero__art" aria-hidden="true">
            <img src="/calcudoku/icons/Icon-192.png" alt="" width="168" height="168" />
          </div>
        </div>
      </header>

      <div className="gi-main">
        <StoreCTA app="dlp" placement="game-page" page="/games/calcudoku" headline="Enjoying Calcudoku? Get eight new logic puzzles every day in our free app" />

        <section className="gi-section">
          <p className="gi-lead">
            <strong>Calcudoku puzzles</strong>, also called <strong>math cage puzzles</strong>, combine
            the <strong>Latin square logic</strong> of Sudoku with <strong>arithmetic cage constraints</strong>.
            Fill the grid so each row and column contains every number from 1 to N, while satisfying
            the math target in each cage. Calcudoku is a{' '}
            <strong>math logic puzzle</strong> that sharpens both your reasoning and number skills.
          </p>
        </section>

        <section className="gi-section" id="how-to-play">
          <h2>How to Solve Calcudoku Puzzles</h2>
          <div className="gi-grid">
            <div className="gi-card">
              <span className="gi-card__num">01</span>
              <h3>Latin Square Rule</h3>
              <p>Each row and column must contain every number from 1 to N exactly once, where N is the grid size.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">02</span>
              <h3>Cage Constraints</h3>
              <p>Each cage has a target number and operation. The numbers in the cage must produce the target using that operation.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">03</span>
              <h3>Single-Cell Cages</h3>
              <p>A cage with one cell and no operation tells you exactly what number goes there - it is a free clue.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">04</span>
              <h3>Candidate Lists</h3>
              <p>For each cage, list all number combinations that produce the target. Then eliminate based on row/column constraints.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">05</span>
              <h3>Subtraction and Division</h3>
              <p>These operations only appear in 2-cell cages. The order of numbers does not matter - only the result does.</p>
            </div>
            <div className="gi-card">
              <span className="gi-card__num">06</span>
              <h3>Progressive Difficulty</h3>
              <p>Start with 3x3 and 4x4 grids to learn the basics, then move to 6x6 and 9x9 for a real challenge.</p>
            </div>
          </div>
        </section>

        <section className="gi-section">
          <h2>Advanced Calcudoku Solving Techniques</h2>
          <p>
            Beyond basic cage arithmetic, advanced <strong>Calcudoku</strong> solvers use constraint propagation
            and cross-referencing to tackle larger grids efficiently.
          </p>
          <ul className="gi-list">
            <li>Enumerate all valid combinations for each cage, then cross-reference with row/column constraints</li>
            <li>Look for "naked pairs" - two cells in the same row/column that can only be two specific numbers</li>
            <li>Use parity reasoning: in a subtraction cage, one number must be larger than the other</li>
            <li>For multiplication cages, factor the target to find valid combinations quickly</li>
            <li>Work on small cages first, as they have fewer possible combinations</li>
            <li>When stuck, focus on rows or columns with the most filled cells</li>
          </ul>
        </section>

        <section className="gi-section">
          <h2>Calcudoku Puzzle FAQ</h2>
          <div className="gi-faq">
            <details open>
              <summary>Is Calcudoku the same as Sudoku?</summary>
              <p>They share the Latin square rule, but Calcudoku adds arithmetic cages instead of pre-filled numbers. Calcudoku requires both logic and basic math.</p>
            </details>
            <details>
              <summary>Can numbers repeat in a cage?</summary>
              <p>Yes, as long as repeating numbers are not in the same row or column. This makes cage analysis more interesting.</p>
            </details>
            <details>
              <summary>What grid sizes are available?</summary>
              <p>Our game has 560 Calcudoku puzzles, from 3x3 starter grids to 9x9 expert grids, plus a new daily puzzle. Larger grids use numbers 1-9 and require more advanced strategies.</p>
            </details>
          </div>
        </section>

        <section className="gi-cta">
          <h2>Ready to Crunch Some Numbers?</h2>
          <p>
            Test your <strong>arithmetic and logic skills</strong> with <strong>Calcudoku puzzles</strong>.
            From 3x3 starter grids to challenging 9x9 puzzles, every solution is reachable through
            pure deduction.
          </p>
          <div className="gi-cta__actions">
            <Link to="/printable/calcudoku" className="button">Print Calcudoku Puzzles</Link>
            <Link to="/logic-games" className="button secondary">More Logic Puzzles</Link>
          </div>
        </section>
      </div>
    </div>
  );
}

export default CalcudokuPage;
